/**
 * 工作流图：画布与渲染管线共用的一份定义。
 *
 * 画布上的连线以前是写死的示意图，现在是真的会被执行的东西——所以校验必须
 * 前后端同一份，不然 UI 让你连的图管线会拒绝跑。这个文件不依赖 React 也不
 * 依赖数据库，两边直接 import。
 */

export interface GraphEdge {
  from: string;
  to: string;
  /** Named input port on modules that expose more than one target handle. */
  targetInput?: string;
}

export interface GraphNodeSpec {
  key: string;
  x: number;
  y: number;
}

/** 模块的上游依赖：键=模块，值=可接受的输入模块 */
const ACCEPTS: Record<string, string[]> = {
  llm: ["product", "topic", "value"],
  script: ["product", "topic", "llm", "value"],
  keywords: ["script", "llm", "value"],
  materials: ["product", "keywords", "media"],
  tts: ["script", "voice"],
  subtitles: ["script", "tts"],
  bgm: ["script"],
  prompt: ["product", "topic", "script", "media", "value"],
  image_compose: ["avatar", "media"],
  aigc: ["product", "topic", "script", "prompt", "avatar", "voice", "media", "value"],
  infinitetalk: ["script", "prompt", "avatar", "voice", "tts", "media", "value", "image_compose"],
  compose: ["materials", "tts", "subtitles", "bgm", "media"],
  render: ["script", "prompt", "aigc", "infinitetalk", "avatar", "voice", "tts", "compose"],
  chroma: ["render", "infinitetalk", "media"],
  output: ["render", "infinitetalk", "chroma"],
  publish: ["output"],
};

/** render 必须拿到的东西——没有脚本就没有可播的内容 */
const REQUIRED_INTO_RENDER = ["script"];

export const INFINITETALK_INPUT_PORTS = [
  { id: "source", label: "Photo" },
  { id: "prompt", label: "Prompt" },
  { id: "speech_audio", label: "TTS audio" },
] as const;

const INFINITETALK_PORT_SOURCES: Record<string, string[]> = {
  source: ["avatar", "media", "image_compose"],
  prompt: ["prompt"],
  speech_audio: ["script", "voice", "tts"],
};

/** Attach pre-v4 InfiniteTalk edges to the closest matching named input. */
export function inferTargetInput(
  from: string,
  to: string,
): string | undefined {
  if (to !== "infinitetalk") return undefined;
  if (from === "avatar") return "source";
  if (from === "media") return "source";
  if (from === "image_compose") return "source";
  if (from === "prompt") return "prompt";
  if (from === "script" || from === "voice" || from === "tts") {
    return "speech_audio";
  }
  return undefined;
}

/** 没有存过 edges 的老模板：按原来的固定接线还原 */
export function defaultEdges(nodeKeys: string[]): GraphEdge[] {
  const has = (k: string) => nodeKeys.includes(k);
  const wires: GraphEdge[] = [];
  if (has("topic") && has("llm")) wires.push({ from: "topic", to: "llm" });
  if (has("product") && has("llm")) wires.push({ from: "product", to: "llm" });
  if (has("llm") && has("script")) wires.push({ from: "llm", to: "script" });
  if (has("topic") && has("script") && !has("llm")) wires.push({ from: "topic", to: "script" });
  if (has("product") && has("script") && !has("llm")) wires.push({ from: "product", to: "script" });
  if (has("script") && has("keywords")) wires.push({ from: "script", to: "keywords" });
  if (has("product") && has("materials")) wires.push({ from: "product", to: "materials" });
  if (has("keywords") && has("materials")) wires.push({ from: "keywords", to: "materials" });
  if (has("script") && has("tts")) wires.push({ from: "script", to: "tts" });
  if (has("voice") && has("tts")) wires.push({ from: "voice", to: "tts" });
  if (has("tts") && has("subtitles")) wires.push({ from: "tts", to: "subtitles" });
  if (has("script") && has("bgm")) wires.push({ from: "script", to: "bgm" });
  if (has("materials") && has("compose")) wires.push({ from: "materials", to: "compose" });
  if (has("tts") && has("compose")) wires.push({ from: "tts", to: "compose" });
  if (has("subtitles") && has("compose")) wires.push({ from: "subtitles", to: "compose" });
  if (has("bgm") && has("compose")) wires.push({ from: "bgm", to: "compose" });
  if (has("compose") && has("render")) wires.push({ from: "compose", to: "render" });
  if (has("script") && has("prompt")) wires.push({ from: "script", to: "prompt" });
  if (has("product") && has("prompt")) wires.push({ from: "product", to: "prompt" });
  if (has("media") && has("prompt")) wires.push({ from: "media", to: "prompt" });
  if (has("avatar") && has("image_compose")) wires.push({ from: "avatar", to: "image_compose" });
  if (has("media") && has("image_compose")) wires.push({ from: "media", to: "image_compose" });
  if (has("prompt") && has("aigc")) wires.push({ from: "prompt", to: "aigc" });
  if (has("script") && has("aigc") && !has("prompt")) wires.push({ from: "script", to: "aigc" });
  if (has("aigc") && has("render")) wires.push({ from: "aigc", to: "render" });
  if (has("script") && has("infinitetalk")) {
    if (!has("tts")) {
      wires.push({ from: "script", to: "infinitetalk", targetInput: "speech_audio" });
    }
  }
  if (has("prompt") && has("infinitetalk"))
    wires.push({ from: "prompt", to: "infinitetalk", targetInput: "prompt" });
  if (has("avatar") && has("infinitetalk"))
    wires.push({ from: "avatar", to: "infinitetalk", targetInput: "source" });
  if (has("image_compose") && has("infinitetalk"))
    wires.push({ from: "image_compose", to: "infinitetalk", targetInput: "source" });
  if (has("voice") && has("infinitetalk") && !has("tts"))
    wires.push({ from: "voice", to: "infinitetalk", targetInput: "speech_audio" });
  if (has("tts") && has("infinitetalk"))
    wires.push({ from: "tts", to: "infinitetalk", targetInput: "speech_audio" });
  if (has("infinitetalk") && has("render")) wires.push({ from: "infinitetalk", to: "render" });
  if (has("prompt") && has("render") && !has("aigc")) wires.push({ from: "prompt", to: "render" });
  if (has("script") && has("render") && !has("prompt") && !has("aigc") && !has("infinitetalk") && !has("compose"))
    wires.push({ from: "script", to: "render" });
  if (has("avatar") && has("render") && !has("infinitetalk")) wires.push({ from: "avatar", to: "render" });
  if (has("voice") && has("render") && !has("infinitetalk")) wires.push({ from: "voice", to: "render" });
  if (has("render") && has("chroma")) wires.push({ from: "render", to: "chroma" });
  if (has("infinitetalk") && !has("render") && has("chroma")) wires.push({ from: "infinitetalk", to: "chroma" });
  if (has("media") && has("chroma")) wires.push({ from: "media", to: "chroma" });
  if (has("chroma") && has("output")) wires.push({ from: "chroma", to: "output" });
  // chroma 在场时由它接管到 output 的这一段
  if (has("render") && has("output") && !has("chroma"))
    wires.push({ from: "render", to: "output" });
  if (has("infinitetalk") && !has("render") && has("output") && !has("chroma"))
    wires.push({ from: "infinitetalk", to: "output" });
  if (has("output") && has("publish")) wires.push({ from: "output", to: "publish" });
  return wires;
}

export function defaultFacelessCanvas(): {
  nodes: GraphNodeSpec[];
  edges: GraphEdge[];
} {
  const nodes: GraphNodeSpec[] = [
    { key: "topic", x: 0, y: 0 },
    { key: "product", x: 0, y: 210 },
    { key: "script", x: 280, y: 0 },
    { key: "keywords", x: 560, y: 0 },
    { key: "materials", x: 840, y: 100 },
    { key: "tts", x: 560, y: 280 },
    { key: "subtitles", x: 840, y: 310 },
    { key: "bgm", x: 840, y: 520 },
    { key: "compose", x: 1120, y: 250 },
    { key: "render", x: 1400, y: 250 },
    { key: "output", x: 1680, y: 250 },
  ];
  // Topic owns narration generation. Product is an optional, high-priority
  // visual source for the hybrid material router, so it must not make script
  // generation depend on a product record being present.
  const edges = defaultEdges(nodes.map((node) => node.key)).filter(
    (edge) => !(edge.from === "product" && edge.to === "script"),
  );
  return { nodes, edges };
}

/** Focused setup-to-talking-avatar flow used by the InfiniteTalk type. */
export function defaultInfiniteTalkCanvas(): {
  nodes: GraphNodeSpec[];
  edges: GraphEdge[];
} {
  const nodes: GraphNodeSpec[] = [
    { key: "avatar", x: 0, y: 0 },
    { key: "media", x: 0, y: 230 },
    { key: "voice", x: 0, y: 460 },
    { key: "script", x: 0, y: 690 },
    { key: "tts", x: 310, y: 500 },
    { key: "prompt", x: 310, y: 100 },
    { key: "image_compose", x: 310, y: 280 },
    { key: "infinitetalk", x: 650, y: 300 },
    { key: "output", x: 990, y: 300 },
  ];
  const edges: GraphEdge[] = [
    { from: "media", to: "prompt" },
    { from: "script", to: "prompt" },
    { from: "script", to: "tts" },
    { from: "voice", to: "tts" },
    { from: "tts", to: "infinitetalk", targetInput: "speech_audio" },
    { from: "prompt", to: "infinitetalk", targetInput: "prompt" },
    { from: "avatar", to: "image_compose" },
    { from: "media", to: "image_compose" },
    { from: "image_compose", to: "infinitetalk", targetInput: "source" },
    { from: "infinitetalk", to: "output" },
  ];
  return { nodes, edges };
}

/** 单条连线是否合法（画布上实时拦截用） */
export function isEdgeAllowed(
  from: string,
  to: string,
  targetInput?: string | null,
): boolean {
  if (!(ACCEPTS[to]?.includes(from) ?? false)) return false;
  if (to !== "infinitetalk" || !targetInput) return true;
  return INFINITETALK_PORT_SOURCES[targetInput]?.includes(from) ?? false;
}

function reaches(edges: GraphEdge[], from: string, to: string): boolean {
  const seen = new Set<string>();
  const stack = [from];
  while (stack.length) {
    const cur = stack.pop()!;
    if (cur === to) return true;
    if (seen.has(cur)) continue;
    seen.add(cur);
    for (const e of edges) if (e.from === cur) stack.push(e.to);
  }
  return false;
}

function hasCycle(nodeKeys: string[], edges: GraphEdge[]): boolean {
  const state = new Map<string, 0 | 1 | 2>();
  const walk = (n: string): boolean => {
    if (state.get(n) === 1) return true;
    if (state.get(n) === 2) return false;
    state.set(n, 1);
    for (const e of edges) if (e.from === n && walk(e.to)) return true;
    state.set(n, 2);
    return false;
  };
  return nodeKeys.some(walk);
}

/**
 * 整图校验。返回的错误直接展示给管理员，所以措辞要说清楚"哪里断了"，
 * 而不是"图无效"。
 */
export function validateGraph(
  nodeKeys: string[],
  edges: GraphEdge[],
): string[] {
  const errors: string[] = [];
  const has = (k: string) => nodeKeys.includes(k);

  for (const e of edges) {
    if (!has(e.from) || !has(e.to)) continue; // 节点被删掉的残留连线，重建时会丢弃
    if (!isEdgeAllowed(e.from, e.to, e.targetInput)) {
      errors.push(`${e.from} → ${e.to} isn't a step the pipeline can run`);
    }
  }

  if (hasCycle(nodeKeys, edges)) {
    errors.push("The graph loops back on itself — a job would never finish");
  }

  if (has("image_compose")) {
    if (!edges.some((edge) => edge.from === "avatar" && edge.to === "image_compose")) {
      errors.push("Image composer requires a person reference from the avatar module");
    }
    if (!edges.some((edge) => edge.from === "media" && edge.to === "image_compose")) {
      errors.push("Image composer requires a background reference from the media module");
    }
  }

  if (has("infinitetalk")) {
    const hasPortInput = (input: string, sources: string[]) =>
      edges.some(
        (edge) =>
          edge.to === "infinitetalk" &&
          sources.includes(edge.from) &&
          (!edge.targetInput || edge.targetInput === input),
      );
    if (!hasPortInput("source", ["avatar", "media", "image_compose"])) {
      errors.push("InfiniteTalk requires an image or video source wired into the module");
    }
    const hasGeneratedAudio =
      has("tts") &&
      hasPortInput("speech_audio", ["tts"]) &&
      reaches(edges, "script", "tts");
    if (
      !has("script") ||
      (!hasPortInput("speech_audio", ["script"]) && !hasGeneratedAudio)
    ) {
      errors.push("InfiniteTalk requires script text or generated audio wired into the module");
    }
    if (!has("prompt") || !hasPortInput("prompt", ["prompt"])) {
      errors.push("InfiniteTalk requires a generated prompt wired into the module");
    }
    const hasSpeechInput =
      (has("voice") && hasPortInput("speech_audio", ["voice"])) ||
      hasGeneratedAudio;
    if (!hasSpeechInput) {
      errors.push("InfiniteTalk requires a voice or audio source wired into the module");
    }
  }

  const renderer = has("render")
    ? "render"
    : has("infinitetalk")
      ? "infinitetalk"
      : null;
  if (!renderer) {
    errors.push("No render module — nothing would produce a video");
  } else {
    for (const req of REQUIRED_INTO_RENDER) {
      if (has(req) && !reaches(edges, req, renderer)) {
        errors.push(`${req} isn't wired into ${renderer}`);
      }
    }
    if (has("output") && !reaches(edges, renderer, "output")) {
      errors.push(`${renderer} can't reach output — finished videos would go nowhere`);
    }
  }

  if (!has("output")) {
    errors.push("No output module — there'd be nowhere to store the result");
  }

  return errors;
}

/**
 * render 之后到 output 之间要依次经过的加工步骤。管线按这个决定成片
 * 完成后还要做什么；返回空数组表示渲染完直接归档。
 */
export function postRenderChain(
  nodeKeys: string[],
  edges: GraphEdge[],
): string[] {
  const chain: string[] = [];
  let cur = nodeKeys.includes("render")
    ? "render"
    : nodeKeys.includes("infinitetalk")
      ? "infinitetalk"
      : "render";
  const seen = new Set<string>([cur]);
  while (true) {
    const next = edges.find((e) => e.from === cur && nodeKeys.includes(e.to));
    if (!next || next.to === "output" || seen.has(next.to)) break;
    chain.push(next.to);
    seen.add(next.to);
    cur = next.to;
  }
  return chain;
}
